import { defineStore } from 'pinia';
import { ref } from 'vue';
import { db } from '../lib/firebase';
import { 
  collection, getDocs, getDoc, doc, setDoc, 
  deleteDoc, addDoc, query, where, onSnapshot 
} from 'firebase/firestore';

export const useCatalogueStore = defineStore('catalogue', () => {
  const dishes = ref([]);
  const extras = ref([]);
  const portions = ref([]);
  const b2bOffers = ref([]);
  const reviews = ref([]);
  const loading = ref(false);

  let unsubProducts = null;
  let unsubPortions = null;
  let unsubB2BOffers = null;
  let unsubReviews = null;

  // --- DISHES & EXTRAS ---

  // Shared logic to process product snapshots into dishes/extras
  const _processProducts = (docs) => {
    const normalizeChannels = (channels) => {
      if (!channels) return [];
      if (Array.isArray(channels)) return channels.map(c => String(c).toLowerCase());
      return [String(channels).toLowerCase()];
    };
    const normalizeCategory = (value) => String(value || 'Plat').trim().toLowerCase();

    const allProducts = docs.map(d => ({
      id: d.id,
      ...d.data(),
      channels: normalizeChannels(d.data().channels)
    }));

    dishes.value = allProducts.filter(p => normalizeCategory(p.category) !== 'extra');
    extras.value = allProducts.filter(p => normalizeCategory(p.category) === 'extra');
    loading.value = false;

    // Cache image URLs for instant restore on next visit (avoids waiting for Firestore)
    _cacheImageUrls(allProducts);
  };

  // --- Image URL caching for instant display on return visits ---
  const _cacheImageUrls = (products) => {
    try {
      const toMs = (v) => { if (!v) return 0; if (v.toDate) return v.toDate().getTime(); return new Date(v).getTime() || 0; };
      const imageData = products
        .filter(p => p.image || p.thumbnail)
        .map(p => ({ id: p.id, image: p.image || null, thumbnail: p.thumbnail || null, category: p.category || 'Plat', name: p.name || '', active: p.active, createdAt: toMs(p.createdAt), updatedAt: toMs(p.updatedAt) }));
      sessionStorage.setItem('clocook_product_images', JSON.stringify({ data: imageData, ts: Date.now() }));
    } catch (e) { /* sessionStorage full or unavailable */ }
  };

  const _restoreCachedProducts = () => {
    try {
      const cached = sessionStorage.getItem('clocook_product_images');
      if (!cached) return false;
      const { data, ts } = JSON.parse(cached);
      // Cache valid for 2 hours
      if (Date.now() - ts > 7200000) return false;
      // Restore minimal product data with images so the UI can start rendering
      const normalizeCategory = (value) => String(value || 'Plat').trim().toLowerCase();
      dishes.value = data.filter(p => normalizeCategory(p.category) !== 'extra');
      extras.value = data.filter(p => normalizeCategory(p.category) === 'extra');
      return true;
    } catch (e) { return false; }
  };

  let _initialFetchDone = false;

  async function fetchProducts() {
    if (_initialFetchDone || unsubProducts) return; // Already done or listening
    _initialFetchDone = true;

    loading.value = true;

    // Phase 0: Instantly restore cached images (no Firestore wait)
    const hadCache = _restoreCachedProducts();
    if (hadCache) loading.value = false; // UI can render immediately with cache

    const q = query(collection(db, 'products'));

    // Phase 1: Fast one-shot getDocs (closes connection → network goes idle for Lighthouse)
    try {
      const snapshot = await getDocs(q);
      _processProducts(snapshot.docs);
    } catch (error) {
      console.error("Error fetching products (getDocs):", error);
      loading.value = false;
    }

    // Phase 2: Activate real-time listener after browser is idle
    const enableRealtime = () => {
      if (unsubProducts) return;
      unsubProducts = onSnapshot(q, (snapshot) => {
        _processProducts(snapshot.docs);
      }, (error) => {
        console.error("Error in products onSnapshot:", error);
      });
    };

    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      requestIdleCallback(enableRealtime, { timeout: 5000 });
    } else if (typeof window !== 'undefined') {
      setTimeout(enableRealtime, 3000);
    } else {
      enableRealtime();
    }
  }

  async function saveProduct(product) {
    try {
      // Determine collection based on intended category if needed, but we use 'products' for all
      const data = { ...product };
      delete data.id; // Start clean

      if (product.id === 'NEW' || !product.id) {
        const colRef = collection(db, 'products');
        const ref = await addDoc(colRef, data);
        // Listener will update state
        return ref.id;
      } else {
        const docRef = doc(db, 'products', product.id);
        await setDoc(docRef, data, { merge: true });
        // Listener will update state
        return product.id;
      }
    } catch (error) {
      console.error("Error saving product:", error);
      throw error;
    }
  }

  async function deleteProduct(productId, category) {
    try {
      await deleteDoc(doc(db, 'products', productId));
      // Listener will update state
    } catch (error) {
      console.error("Error deleting product:", error);
    }
  }

  // --- CONFIGS (Portions & B2B) ---

  function fetchConfigs() {
    // Portions
    if (!unsubPortions) {
        unsubPortions = onSnapshot(doc(db, 'configs', 'portions'), (docSnap) => {
            if (docSnap.exists()) {
                portions.value = docSnap.data().list || [];
            } else {
                // Init default mock data if empty
                portions.value = [
                    { id: 1, label: 'Individuel', personCount: 1, description: 'Pour 1 personne', price: 12.00, active: true },
                    { id: 2, label: 'Duo', personCount: 2, description: 'Pour 2 personnes', price: 22.00, active: true },
                    { id: 3, label: 'Famille (4)', personCount: 4, description: 'Pour 4 personnes', price: 40.00, active: true }
                ];
            }
        }, error => console.error("Error listening portions:", error));
    }

    // B2B Offers
    if (!unsubB2BOffers) {
        unsubB2BOffers = onSnapshot(doc(db, 'configs', 'b2b_offers'), (docSnap) => {
            if (docSnap.exists()) {
                b2bOffers.value = docSnap.data().list || [];
            }
        }, error => console.error("Error listening b2b_offers:", error));
    }
  }

  // --- REVIEWS ---

  function fetchReviews() {
    if (unsubReviews) return;

    const q = query(collection(db, 'reviews'));
    unsubReviews = onSnapshot(q, (snapshot) => {
        reviews.value = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data({ serverTimestamps: 'estimate' })
        }));
    }, error => console.error("Error listening reviews:", error));
  }

  async function saveConfig(type, listData) {
    // type: 'portions' or 'b2b_offers'
    try {
      await setDoc(doc(db, 'configs', type), { list: listData });
      // Listener will update state
    } catch (error) {
      console.error(`Error saving ${type}:`, error);
    }
  }

  return {
    dishes,
    extras,
    portions,
    b2bOffers,
    reviews,
    loading,
    fetchProducts,
    saveProduct,
    deleteProduct,
    fetchConfigs,
    saveConfig,
    fetchReviews
  };
});
